import { useReducer, useMemo, useCallback } from "react"

export type EstimateSourceType = `annual` | `bill` | `default`

export interface EstimateSourceData {
  type: EstimateSourceType
  annualUsage?: number
  billAmount?: number
  billStartMonth?: number
  billEndMonth?: number
}

type EstimateSourceAction =
  | {
      type: `SET_BILL`
      payload: {
        billAmount: number
        billStartMonth: number
        billEndMonth: number
      }
    }
  | { type: `RESET` }
  | { type: `SET_ANNUAL`; payload: { annualUsage: number } }
  | { type: `SET_DEFAULT` }

const initialState: EstimateSourceData | null = null

function estimateSourceReducer(
  state: EstimateSourceData | null,
  action: EstimateSourceAction,
): EstimateSourceData | null {
  switch (action.type) {
    case `SET_ANNUAL`:
      return {
        type: `annual`,
        annualUsage: action.payload.annualUsage,
      }
    case `SET_BILL`:
      return {
        type: `bill`,
        billAmount: action.payload.billAmount,
        billStartMonth: action.payload.billStartMonth,
        billEndMonth: action.payload.billEndMonth,
      }
    case `SET_DEFAULT`:
      return { type: `default` }
    case `RESET`:
      return null
    default:
      return state
  }
}

const MONTHS = [
  `Jan`,
  `Feb`,
  `Mar`,
  `Apr`,
  `May`,
  `Jun`,
  `Jul`,
  `Aug`,
  `Sep`,
  `Oct`,
  `Nov`,
  `Dec`,
] as const

interface UseEstimateSourceReturn {
  estimateSourceData: EstimateSourceData | null
  estimateSourceText: string | null
  setAnnualSource: (annualUsage: number) => void
  setBillSource: (
    billAmount: number,
    billStartMonth: number,
    billEndMonth: number,
  ) => void
  setDefaultSource: () => void
  resetSource: () => void
}

export const useEstimateSource = (
  utilityRateDisplay: string | null,
): UseEstimateSourceReturn => {
  const [estimateSourceData, dispatch] = useReducer(
    estimateSourceReducer,
    initialState,
  )

  const setAnnualSource = useCallback((annualUsage: number) => {
    dispatch({ type: `SET_ANNUAL`, payload: { annualUsage } })
  }, [])

  const setBillSource = useCallback(
    (billAmount: number, billStartMonth: number, billEndMonth: number) => {
      dispatch({
        type: `SET_BILL`,
        payload: { billAmount, billStartMonth, billEndMonth },
      })
    },
    [],
  )

  const setDefaultSource = useCallback(() => {
    dispatch({ type: `SET_DEFAULT` })
  }, [])

  const resetSource = useCallback(() => {
    dispatch({ type: `RESET` })
  }, [])

  const estimateSourceText = useMemo(() => {
    if (!estimateSourceData) return null

    const rateText = utilityRateDisplay || `selected rate`

    if (
      estimateSourceData.type === `bill` &&
      estimateSourceData.billAmount &&
      estimateSourceData.billStartMonth !== undefined
    ) {
      const startMonthName = MONTHS[estimateSourceData.billStartMonth]
      const effectiveEndMonth =
        estimateSourceData.billEndMonth ?? estimateSourceData.billStartMonth
      const endMonthName = MONTHS[effectiveEndMonth]
      const monthRange =
        startMonthName === endMonthName
          ? startMonthName
          : `${startMonthName}-${endMonthName}`
      const formattedAmount = estimateSourceData.billAmount.toLocaleString(
        undefined,
        {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        },
      )

      return `${monthRange} bill of $${formattedAmount} at ${rateText}`
    }

    if (
      estimateSourceData.type === `annual` &&
      estimateSourceData.annualUsage !== undefined
    ) {
      const formattedAnnualUsage = estimateSourceData.annualUsage.toLocaleString(
        undefined,
        {
          maximumFractionDigits: 0,
        },
      )

      return `${formattedAnnualUsage} kWh annually at ${rateText}`
    }

    // For default type, just return the rate text
    // since "Smart Estimates from" will be prepended by the component
    return rateText
  }, [estimateSourceData, utilityRateDisplay])

  return {
    estimateSourceData,
    estimateSourceText,
    setAnnualSource,
    setBillSource,
    setDefaultSource,
    resetSource,
  }
}
